/**
 * Platform layer for budget alerts (80% / 100%).
 *
 * Detection lives in shared (`checkBudgetAlerts`, pure with respect to
 * notifications); here we decide HOW to notify (notification adapter) and
 * avoid repeating the same alert on the same day with a key in localStorage by
 * `budget + period + threshold` (`dedupeKey` already carried by each alert).
 *
 * Trigger points:
 *   - authenticated post-boot (boot-manager → usePostBootFinanceTasks)
 *   - upon loading /budgets page
 */
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import type { PowerSyncDrizzleDB } from "@cashsync/powersync";
import {
  budgetAlertThresholds,
  checkBudgetAlerts,
  formatMoney,
  todayDateKey,
} from "@cashsync/shared";

import { appConfig } from "@/lib/app-config";
import { getNotificationAdapter } from "./service";

const STORAGE_KEY = "budget-alerts.v1";
const PERCENT = 100;

/**
 * NARROW signature on purpose, same as `useI18n().t`: only `MessageDescriptor`.
 * Accepting `string` reopens the door to text that extractor cannot see.
 */
type Translate = (message: MessageDescriptor, values?: Record<string, unknown>) => string;

function getStorage(): Storage | null {
  try {
    return globalThis.localStorage;
  } catch {
    return null;
  }
}

/** dedupeKey → YYYY-MM-DD map of last notification. */
function readNotified(): Record<string, string> {
  const raw = getStorage()?.getItem(STORAGE_KEY);
  if (!raw) {
    return {};
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
      return {};
    }
    const result: Record<string, string> = {};
    for (const [key, value] of Object.entries(parsed)) {
      if (typeof value === "string") {
        result[key] = value;
      }
    }
    return result;
  } catch {
    return {};
  }
}

function writeNotified(map: Record<string, string>, todayKey: string): void {
  const storage = getStorage();
  if (!storage) {
    return;
  }
  // Pruning: dedupe is per day — entries from previous days no longer contribute.
  const pruned: Record<string, string> = {};
  for (const [key, value] of Object.entries(map)) {
    if (value === todayKey) {
      pruned[key] = value;
    }
  }
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(pruned));
  } catch {
    // Quota/private mode: non-fatal — at most notification repeats.
  }
}

/**
 * Evaluates user budgets and displays a local notification for
 * each crossed threshold (only highest per budget, max 1 per day).
 * Silent without notification permission (not requested in background).
 */
export async function checkAndNotifyBudgetAlerts(
  db: PowerSyncDrizzleDB,
  userId: string,
  t: Translate,
  locale?: string,
): Promise<void> {
  let alerts;
  try {
    // CONFIG_OVER_CODE: thresholds are decided by platform
    // (`finance.budget_alert_threshold_pcts`); without config they are
    // usual literals (100% / 80%). Config decides WHEN to notify —
    // spending and budget are untouched.
    alerts = await checkBudgetAlerts(db, userId, undefined, budgetAlertThresholds(appConfig()));
  } catch (err: unknown) {
    console.warn("[budget-alerts] check failed:", err instanceof Error ? err.message : err);
    return;
  }
  if (alerts.length === 0) {
    return;
  }

  const todayKey = todayDateKey();
  const notified = readNotified();
  const pending = alerts.filter((alert) => notified[alert.dedupeKey] !== todayKey);
  if (pending.length === 0) {
    return;
  }

  const adapter = await getNotificationAdapter();
  const permission = await adapter.getPermission();
  if (permission !== "granted") {
    // Without permission do not mark dedupe: if user grants later,
    // next daily check can notify.
    return;
  }

  for (const alert of pending) {
    const percent = Math.round(alert.ratio * PERCENT);
    const spent = formatMoney(alert.spent, alert.baseCurrency, locale);
    const limit = formatMoney(alert.budgeted, alert.baseCurrency, locale);
    const title = alert.threshold >= 1 ? t(msg`Budget exceeded`) : t(msg`Budget almost reached`);
    const body = t(msg`You have used ${percent}% of your budget (${spent} of ${limit}).`);
    try {
      await adapter.show(title, {
        body,
        tag: alert.dedupeKey,
        data: { budgetId: alert.budgetId },
      });
      notified[alert.dedupeKey] = todayKey;
    } catch (err: unknown) {
      console.warn("[budget-alerts] show failed:", err instanceof Error ? err.message : err);
    }
  }
  writeNotified(notified, todayKey);
}
